import { defineStore } from "pinia";
import api from "../services/api";
import { useGameStore } from "./game";

function asList(value) {
  return Array.isArray(value) ? value : [];
}

export const useMarketStore = defineStore("market", {
  state: () => ({
    cardListings: [],
    myListings: [],
    myOffers: [],
    isLoading: false,
    loadError: "",
    cardPage: 1,
    cardTotalPages: 1,
    cardLimit: 20,
  }),
  actions: {
    applyResources(payload) {
      if (!payload?.resources) return;
      useGameStore().mergeEmpireStocks(payload.resources);
    },

    async refreshPlayerCards() {
      await useGameStore().fetchPlayerCards();
    },

    async fetchCardListings({ page, search, rarity, type, limit } = {}) {
      this.isLoading = true;
      this.loadError = "";
      const params = {
        page: page ?? this.cardPage,
        limit: limit ?? this.cardLimit,
      };
      const q = String(search || "").trim();
      if (q) params.search = q;
      if (rarity) params.rarity = rarity;
      if (type) params.type = type;
      try {
        const response = await api.get("/api/market/cards", { params });
        this.cardListings = asList(response.data?.data);
        this.cardPage = Number(response.data?.currentPage) || 1;
        this.cardTotalPages = Math.max(1, Number(response.data?.totalPages) || 1);
        return this.cardListings;
      } catch (error) {
        this.cardListings = [];
        this.cardPage = 1;
        this.cardTotalPages = 1;
        this.loadError =
          error.response?.data?.message || "Could not load the card market.";
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchMyCardMarket() {
      try {
        const response = await api.get("/api/market/cards/mine");
        this.myListings = asList(response.data?.listings);
        this.myOffers = asList(response.data?.offers);
        return response.data;
      } catch (error) {
        this.myListings = [];
        this.myOffers = [];
        throw error;
      }
    },

    async listCard({ playerCardId, price }) {
      const response = await api.post("/api/market/cards/list", {
        player_card_id: playerCardId,
        price,
      });
      const listing = response.data?.listing;
      if (listing) {
        this.cardListings = [listing, ...this.cardListings];
        this.myListings = [listing, ...this.myListings];
      }
      this.applyResources(response.data);
      await this.refreshPlayerCards();
      return response.data;
    },

    async buyCard(listingId) {
      const response = await api.post("/api/market/cards/buy", {
        listing_id: listingId,
      });
      const id = Number(listingId);
      this.cardListings = this.cardListings.filter(
        (row) => Number(row.listing_id) !== id
      );
      this.myListings = this.myListings.filter(
        (row) => Number(row.listing_id) !== id
      );
      this.myOffers = this.myOffers.filter(
        (row) => Number(row.listing_id) !== id
      );
      this.applyResources(response.data);
      await this.refreshPlayerCards();
      return response.data;
    },

    async makeOffer({ listingId, offerPrice }) {
      const response = await api.post("/api/market/cards/offer", {
        listing_id: listingId,
        offer_price: offerPrice,
      });
      const offer = response.data?.offer;
      if (offer) {
        const listing =
          this.cardListings.find(
            (row) => Number(row.listing_id) === Number(listingId)
          ) ||
          this.myListings.find(
            (row) => Number(row.listing_id) === Number(listingId)
          );
        const hydrated = {
          ...offer,
          card: listing?.card,
          expires_at: listing?.expires_at,
          listing_price: listing?.price,
        };
        this.myOffers = [hydrated, ...this.myOffers];
        this.myListings = this.myListings.map((listing) => {
          if (Number(listing.listing_id) !== Number(listingId)) return listing;
          return {
            ...listing,
            offers: [...(listing.offers || []), offer],
          };
        });
      }
      this.applyResources(response.data);
      return response.data;
    },

    async resolveOffer({ offerId, action }) {
      const response = await api.post(
        `/api/market/cards/offer/${offerId}/resolve`,
        { action }
      );
      const id = Number(offerId);
      if (action === "accept") {
        const accepted = response.data?.offer;
        const listingId = Number(accepted?.listing_id);
        this.cardListings = this.cardListings.filter(
          (row) => Number(row.listing_id) !== listingId
        );
        this.myListings = this.myListings.filter(
          (row) => Number(row.listing_id) !== listingId
        );
        this.myOffers = this.myOffers.filter(
          (row) => Number(row.listing_id) !== listingId
        );
        await this.refreshPlayerCards();
      } else {
        this.myListings = this.myListings.map((listing) => ({
          ...listing,
          offers: (listing.offers || []).filter(
            (offer) => Number(offer.offer_id) !== id
          ),
        }));
        this.myOffers = this.myOffers.filter(
          (offer) => Number(offer.offer_id) !== id
        );
      }
      this.applyResources(response.data);
      return response.data;
    },

    async cancelListing(listingId) {
      const response = await api.post("/api/market/cards/cancel", {
        listing_id: listingId,
      });
      const id = Number(listingId);
      this.cardListings = this.cardListings.filter(
        (row) => Number(row.listing_id) !== id
      );
      this.myListings = this.myListings.filter(
        (row) => Number(row.listing_id) !== id
      );
      this.myOffers = this.myOffers.filter(
        (row) => Number(row.listing_id) !== id
      );
      this.applyResources(response.data);
      await this.refreshPlayerCards();
      return response.data;
    },
  },
});
